import { useEffect, useState } from 'react';
import {
  AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { X, Database, Activity, RefreshCw } from 'lucide-react';
import StatusPill, { mapServerStatusToPill } from '../common/StatusPill';
import MetricGauge from './MetricGauge';
import TimeRangeSelector from '../common/TimeRangeSelector';
import { fetchServerMetricsHistory } from '../../api/api';
import { parseMetricValue, isPercentValue, formatDate } from '../../fonctions/fonctions';
import './ServerDrawer.css';

/* ─── Helpers ────────────────────────────────────────────── */

function parseNumeric(raw) {
  if (!raw) return null;
  const { percent } = parseMetricValue(raw);
  if (percent !== null) return percent;
  const m = raw.match(/^([\d.]+)/);
  return m ? parseFloat(m[1]) : null;
}

function axisTime(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function alerteColor(level) {
  if (level === 4) return '#dc2626';
  if (level === 3) return '#ea580c';
  if (level === 2) return '#d97706';
  return '#16a34a';
}

/* ─── Tooltip recharts ───────────────────────────────────── */

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;

  const raw   = payload[0]?.payload?.rawValue;
  const alerte = payload[0]?.payload?.alerte;
  const color  = alerteColor(alerte);

  return (
    <div style={{
      background: 'white', border: '1px solid #e5e7eb',
      borderRadius: 8, padding: '7px 12px', fontSize: '0.78rem',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    }}>
      <div style={{ color: '#9ca3af', marginBottom: 3 }}>
        {new Date(label).toLocaleString('fr-FR')}
      </div>
      <div style={{ color, fontWeight: 700 }}>{raw || payload[0]?.value?.toFixed(1)}</div>
    </div>
  );
}

/* ─── Single metric chart ─────────────────────────────────── */

function MetricChart({ metric }) {
  const points = (metric.points || [])
    .map(pt => ({
      time:     pt.created_at,
      value:    parseNumeric(pt.value),
      rawValue: pt.value,
      alerte:   parseInt(pt.alerte, 10),
    }))
    .filter(pt => pt.value !== null);

  if (points.length === 0) return null;

  const latest = points[points.length - 1];
  const color  = alerteColor(latest.alerte);
  const gradId = `g-${metric.metric_name.replace(/\W/g, '')}`;

  return (
    <div className="drawer-chart-card">
      <div className="drawer-chart-title">
        <span>{metric.intitule || metric.metric_name}</span>
        <span
          className="drawer-chart-badge"
          style={{
            background: `${color}15`,
            color,
            border: `1px solid ${color}35`,
          }}
        >
          {latest.rawValue}
        </span>
      </div>

      <ResponsiveContainer width="100%" height={150}>
        <AreaChart data={points} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={color} stopOpacity={0.18} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis
            dataKey="time" tickFormatter={axisTime}
            tick={{ fontSize: 10, fill: '#9ca3af' }}
            axisLine={{ stroke: '#e5e7eb' }} tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 10, fill: '#9ca3af' }}
            axisLine={false} tickLine={false}
          />
          <Tooltip content={<ChartTooltip />} />
          <Area
            type="monotone" dataKey="value"
            stroke={color} strokeWidth={2}
            fill={`url(#${gradId})`}
            dot={false}
            activeDot={{ r: 4, fill: color, strokeWidth: 0 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ─── ServerDrawer ───────────────────────────────────────── */

function ServerDrawer({ server, onClose }) {
  const [history,  setHistory]  = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [visible,  setVisible]  = useState(false);
  const [hours,    setHours]    = useState(24);

  const isOpen = !!server;

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!server) return;

    setHistory([]);
    setLoading(true);

    fetchServerMetricsHistory(server.serveur_id, hours)
      .then(r => { if (r.success) setHistory(r.data || []); })
      .catch(err => console.error('Erreur historique drawer:', err))
      .finally(() => setLoading(false));
  }, [server, hours]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!isOpen && !visible) return null;

  const metrics   = server?.metrics || [];
  const isOffline = (server?.server_status || '').toLowerCase().includes('hors ligne');
  const pillStatus= isOffline ? 'down' : mapServerStatusToPill(server?.server_status);

  return (
    <>
      <div
        className={`drawer-backdrop ${visible ? 'open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        className={`drawer-panel ${visible ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={`Détails du serveur ${server?.serveur_name}`}
      >
        {/* Header */}
        <div className="drawer-header">
          <div className="drawer-header-left">
            <div className="drawer-header-icon">
              <Database size={18} />
            </div>
            <div>
              <div className="drawer-server-name">{server?.serveur_name}</div>
              <div className="drawer-server-sub">
                {server?.server_type} · {formatDate(server?.serveur_last_update)}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <StatusPill status={pillStatus} label={isOffline ? 'Injoignable' : server?.server_status} />
            <button className="drawer-close" onClick={onClose} aria-label="Fermer">
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="drawer-body">

          {/* Métriques actuelles */}
          {metrics.length > 0 && (
            <div>
              <h3 className="drawer-section-title">
                <Activity size={13} className="drawer-section-title-icon" />
                Métriques actuelles
              </h3>
              <div className="drawer-gauges">
                {[
                  ...metrics.filter(m => isPercentValue(m.value)),
                  ...metrics.filter(m => !isPercentValue(m.value)),
                ].map((m, i) => (
                  <MetricGauge key={i} metric={m} onClick={() => {}} />
                ))}
              </div>
            </div>
          )}

          {/* Historique */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.85rem' }}>
              <h3 className="drawer-section-title" style={{ margin: 0 }}>
                <RefreshCw size={13} className="drawer-section-title-icon" />
                Historique
              </h3>
              <TimeRangeSelector value={hours} onChange={setHours} />
            </div>

            {loading && (
              <div className="drawer-loading">
                <RefreshCw size={14} className="spin" />
                Chargement des graphiques…
              </div>
            )}

            {!loading && history.length === 0 && (
              <div className="drawer-no-history">
                Aucune donnée historique disponible.<br />
                Les graphiques s'alimenteront automatiquement après les premières mises à jour.
              </div>
            )}

            {!loading && history.length > 0 && (
              <div className="drawer-charts">
                {history.map((metric, i) => (
                  <MetricChart key={i} metric={metric} />
                ))}
              </div>
            )}
          </div>

        </div>
      </aside>
    </>
  );
}

export default ServerDrawer;
